import type { Hit } from 'instantsearch.js';
import { useEffect, useState } from 'react';

import {
  EyewearCard,
  WatchCard,
} from '@/components/ProductListingCard/components';
import { useSendClickedAlgoliaEvent } from '@/hooks/useSendAlgoliaEvent';
import {
  getHitListPosition,
  isEyewearHit,
  isWatchHit,
} from '@/integrations/algolia/helpers';
import type { EyewearHit, WatchHit } from '@/integrations/algolia/types';
import type { ProductCatalogModeCtaStyle } from '@/lib/types';

import * as S from './Heros.styles';

export interface HerosProps {
  hits: Hit<WatchHit | EyewearHit>[];
  productCatalogModeCtaStyle?: ProductCatalogModeCtaStyle;
  type: string | null | undefined;
}

export const Heros: React.FC<HerosProps> = ({
  hits,
  productCatalogModeCtaStyle,
  type,
}) => {
  const [innerWidth, setInnerWidth] = useState<number>(0);
  const sendClickedAlgoliaEvent = useSendClickedAlgoliaEvent();
  const sendHitClick = (hit: Hit<WatchHit | EyewearHit>) =>
    sendClickedAlgoliaEvent(hit.objectID, getHitListPosition(hit));

  useEffect(() => {
    setInnerWidth(window.innerWidth);
  }, []);

  if (!hits?.length) {
    return null;
  }

  if (isEyewearHit(hits[0]) && isEyewearHit(hits[1])) {
    return (
      <S.Heros data-test-id='Heros'>
        {type === 'one_hero_product' && (
          <EyewearCard
            cardClickHandler={() => sendHitClick(hits[0])}
            data-test-id='HeroEyewearCard-0'
            hit={hits[0]}
            productCatalogModeCtaStyle={productCatalogModeCtaStyle}
            sizes='(max-width: 600px) 25vw, 360px'
            style={{ ...(innerWidth <= 648 && { display: 'none' }) }}
            type='square'
          />
        )}
        {type === 'two_hero_products' && (
          <>
            <EyewearCard
              cardClickHandler={() => sendHitClick(hits[0])}
              data-test-id='HerosWatchCard-0'
              hit={hits[0]}
              productCatalogModeCtaStyle={productCatalogModeCtaStyle}
              sizes='(max-width: 600px) 25vw, 335px'
              style={{
                width: '48%',
                ...(innerWidth <= 648 && { display: 'none' }),
              }}
              type='two-heros'
            />
            <EyewearCard
              cardClickHandler={() => sendHitClick(hits[1])}
              data-test-id='HerosWatchCard-1'
              hit={hits[1]}
              productCatalogModeCtaStyle={productCatalogModeCtaStyle}
              sizes='(max-width: 600px) 25vw, 335px'
              style={{
                width: '48%',
                ...(innerWidth <= 648 && { display: 'none' }),
              }}
              type='two-heros'
            />
          </>
        )}
      </S.Heros>
    );
  }

  if (!(isWatchHit(hits[0]) && isWatchHit(hits[1]))) {
    return null;
  }

  return (
    <S.Heros data-test-id='Heros'>
      {type === 'one_hero_product' && (
        <WatchCard
          cardClickHandler={() => sendHitClick(hits[0])}
          data-test-id='HerosWatchCard-0'
          hit={hits[0]}
          productCatalogModeCtaStyle={productCatalogModeCtaStyle}
          sizes='(max-width: 600px) 25vw, 360px'
          style={{ ...(innerWidth <= 648 && { display: 'none' }) }}
          type='square'
        />
      )}
      {type === 'two_hero_products' && (
        <>
          <WatchCard
            cardClickHandler={() => sendHitClick(hits[0])}
            data-test-id='HerosWatchCard-0'
            hit={hits[0]}
            productCatalogModeCtaStyle={productCatalogModeCtaStyle}
            sizes='(max-width: 600px) 25vw, 335px'
            style={{
              width: '48%',
              ...(innerWidth <= 648 && { display: 'none' }),
            }}
            type='two-heros'
          />
          <WatchCard
            cardClickHandler={() => sendHitClick(hits[1])}
            data-test-id='HerosWatchCard-1'
            hit={hits[1]}
            productCatalogModeCtaStyle={productCatalogModeCtaStyle}
            sizes='(max-width: 600px) 25vw, 335px'
            style={{
              width: '48%',
              ...(innerWidth <= 648 && { display: 'none' }),
            }}
            type='two-heros'
          />
        </>
      )}
    </S.Heros>
  );
};
